"use client";

import Image from "next/image";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { HeroTrishRoleChat } from "~/components/siteglue/home/hero-trish-role-chat";
import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { AGENT_READY_CTA, PRIMARY_CTA } from "~/lib/siteglue/landing-redesign/copy";
import { getMarketingChrome, SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";

const TRUST = ["No code required", "15-day trial", "Always a Human-in-the-Loop™"] as const;

const HERO_TRISH_SUPPORT =
  "Your agent answers calls, talks with website visitors, captures or schedules appointments when a calendar is connected, and routes the conversation to the right person.";

const HERO_HEADLINE_CLASS =
  "text-balance text-[clamp(2.1rem,3.6vw+1rem,3.5rem)] font-bold leading-[1.05] tracking-tight";

export function SiteGlueHomeHero() {
  const chrome = getMarketingChrome();

  return (
    <section className="relative isolate overflow-hidden bg-[#f7f6f3] text-slate-900">
      <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] max-w-3xl flex-col items-center justify-center px-4 py-10 text-center sm:px-6 sm:py-14">
        <div className="flex w-full flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center gap-3"
          >
            <Image
              src={chrome.logoSrc}
              alt=""
              width={36}
              height={36}
              className="h-9 w-9 rounded-lg object-contain"
              priority
            />
            <span className="text-sm font-semibold tracking-wide text-slate-900">
              {chrome.productName}
            </span>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-950"
              style={{ backgroundColor: SITEGLUE_COLORS.green }}
            >
              Beta
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
          >
            {chrome.heroEyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className={`mt-2 max-w-3xl ${HERO_HEADLINE_CLASS} text-slate-900`}
          >
            {chrome.heroHeadline}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.26 }}
            className="mt-5 w-full"
          >
            <p className="mx-auto mb-5 max-w-xl text-sm leading-relaxed text-slate-500">
              {HERO_TRISH_SUPPORT}
            </p>
            <div className="mb-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href="/start"
                onClick={() =>
                  trackSiteGlueEvent("homepage_primary_cta_click", { destination: "/start" })
                }
                className="inline-flex h-11 items-center justify-center rounded-full bg-[#4f81bd] px-5 text-sm font-semibold text-white hover:opacity-95"
              >
                {PRIMARY_CTA}
              </Link>
              <Link
                href="/agent-ready"
                onClick={() =>
                  trackSiteGlueEvent("homepage_agent_ready_click", { destination: "/agent-ready" })
                }
                className="inline-flex h-11 items-center justify-center rounded-full border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                {AGENT_READY_CTA}
              </Link>
            </div>
            <HeroTrishRoleChat />
            <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-slate-500">
              {TRUST.map((item) => (
                <li key={item} className="flex items-center gap-1.5">
                  <Check
                    className="h-3.5 w-3.5"
                    style={{ color: SITEGLUE_COLORS.green }}
                    aria-hidden
                  />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
